import { useEffect, useState } from "react";

// Dev-only mood/type panel. Not shown to visitors — toggle with Shift+T.
// Mirrors the prototype's tweaks-app: palette, accent, headline, grain, motion.
// State is in-memory only (resets to the production defaults on reload).

type Palette = "paper" | "cafe" | "noir";
type Headline = "anton" | "black" | "serif";

interface TweakState {
  palette: Palette;
  accent: string;
  headline: Headline;
  grain: boolean;
  motion: boolean;
}

const DEFAULTS: TweakState = {
  palette: "cafe",
  accent: "#c2452b",
  headline: "anton",
  grain: true,
  motion: true,
};

const PALETTE_OPTS: { value: Palette; label: string }[] = [
  { value: "paper", label: "Paper" },
  { value: "cafe", label: "Café" },
  { value: "noir", label: "Noir" },
];
const ACCENTS = ["#c2452b", "#9e5a34", "#c2913a", "#6f7a3f"];
const HEADLINE_OPTS: { value: Headline; label: string }[] = [
  { value: "anton", label: "Condensed" },
  { value: "black", label: "Grotesque" },
  { value: "serif", label: "Serif" },
];

function applyTweaks(t: TweakState) {
  const html = document.documentElement;
  html.setAttribute("data-theme", t.palette);
  html.style.setProperty("--verm", t.accent);
  html.setAttribute("data-head", t.headline);
  document.body.classList.toggle("nograin", !t.grain);
  html.classList.toggle("reduce-motion", !t.motion);
}

function isLight(hex: string): boolean {
  const h = hex.replace("#", "");
  const x = h.length === 3 ? h.replace(/./g, (c) => c + c) : h.padEnd(6, "0");
  const n = parseInt(x.slice(0, 6), 16);
  if (Number.isNaN(n)) return true;
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return r * 299 + g * 587 + b * 114 > 148000;
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  const idx = Math.max(0, options.findIndex((o) => o.value === value));
  const n = options.length;
  return (
    <div className="twk-seg" role="radiogroup">
      <div
        className="twk-seg-thumb"
        style={{
          left: `calc(2px + ${idx} * (100% - 4px) / ${n})`,
          width: `calc((100% - 4px) / ${n})`,
        }}
      />
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Tweaks() {
  const [open, setOpen] = useState(false);
  const [t, setT] = useState<TweakState>(DEFAULTS);

  // Apply defaults immediately + on every change.
  useEffect(() => {
    applyTweaks(t);
  }, [t]);

  // Shift+T toggles the panel (ignored while typing in a field).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.shiftKey && (e.key === "T" || e.key === "t")) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!open) return null;

  const set = <K extends keyof TweakState>(key: K, value: TweakState[K]) =>
    setT((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="twk-panel">
      <div className="twk-hd">
        <b>Tweaks</b>
        <button className="twk-x" aria-label="Close tweaks" onClick={() => setOpen(false)}>
          ✕
        </button>
      </div>
      <div className="twk-body">
        <div className="twk-sect">Mood</div>
        <div className="twk-row">
          <div className="twk-lbl">
            <span>Palette</span>
          </div>
          <Segmented value={t.palette} options={PALETTE_OPTS} onChange={(v) => set("palette", v)} />
        </div>
        <div className="twk-row">
          <div className="twk-lbl">
            <span>Accent</span>
          </div>
          <div className="twk-chips" role="radiogroup">
            {ACCENTS.map((c) => {
              const on = c.toLowerCase() === t.accent.toLowerCase();
              return (
                <button
                  key={c}
                  type="button"
                  className="twk-chip"
                  role="radio"
                  aria-checked={on}
                  data-on={on ? "1" : "0"}
                  aria-label={c}
                  title={c}
                  style={{ background: c }}
                  onClick={() => set("accent", c)}
                >
                  {on && (
                    <svg viewBox="0 0 14 14" aria-hidden="true">
                      <path
                        d="M3 7.2 5.8 10 11 4.2"
                        fill="none"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        stroke={isLight(c) ? "rgba(0,0,0,.78)" : "#fff"}
                      />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="twk-sect">Type</div>
        <div className="twk-row">
          <div className="twk-lbl">
            <span>Headline</span>
          </div>
          <Segmented value={t.headline} options={HEADLINE_OPTS} onChange={(v) => set("headline", v)} />
        </div>

        <div className="twk-sect">Texture & Motion</div>
        <div className="twk-row twk-row-h">
          <div className="twk-lbl">
            <span>Paper grain</span>
          </div>
          <button
            type="button"
            className="twk-toggle"
            data-on={t.grain ? "1" : "0"}
            role="switch"
            aria-checked={t.grain}
            onClick={() => set("grain", !t.grain)}
          >
            <i />
          </button>
        </div>
        <div className="twk-row twk-row-h">
          <div className="twk-lbl">
            <span>Animation</span>
          </div>
          <button
            type="button"
            className="twk-toggle"
            data-on={t.motion ? "1" : "0"}
            role="switch"
            aria-checked={t.motion}
            onClick={() => set("motion", !t.motion)}
          >
            <i />
          </button>
        </div>

        <div className="twk-hint">Dev panel · Shift+T to toggle</div>
      </div>
    </div>
  );
}
